import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, ChevronsUpDown, Loader2, Tag } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Search-as-you-type combobox for picking a marketplace category.
 *
 * Powers product/service forms in:
 *  - MerchantDashboard.js (merchant self-edits)
 *  - MerchantServicesManager.js (admin + MRO edit a merchant's listings)
 *
 * Props:
 *   api: axios instance
 *   itemType: 'service' | 'product'
 *   value: currently-selected category_id
 *   onChange: (categoryId, categoryName) => void
 *   placeholder: optional placeholder string
 *   testId: optional data-testid for the trigger button
 */
export function CategoryCombobox({
  api,
  itemType = 'service',
  value,
  onChange,
  placeholder = 'Pick or search a category…',
  testId = 'category-combobox',
}) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Refetch whenever item_type flips (the catalog differs for products vs services).
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/marketplace/categories?type=${itemType || 'service'}`);
      setCategories(data.categories || []);
    } catch (e) {
      // Non-fatal — combobox will show "no results"
    } finally {
      setLoading(false);
    }
  }, [api, itemType]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Flatten the tree + tag each entry with the path label for search.
  const flat = useMemo(() => {
    const rows = [];
    for (const root of categories) {
      rows.push({
        id: root.id,
        name: root.name,
        label: root.name,
        path: root.name,
        depth: 0,
        keywords: (root.keywords || []).join(' '),
      });
      for (const sub of (root.children || [])) {
        rows.push({
          id: sub.id,
          name: sub.name,
          label: sub.name,
          path: `${root.name} › ${sub.name}`,
          depth: 1,
          keywords: (sub.keywords || []).join(' '),
        });
      }
    }
    return rows;
  }, [categories]);

  const selected = flat.find((c) => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-[#000B18] border-white/20 text-white hover:bg-[#000B18] hover:text-white h-10"
          data-testid={testId}
        >
          <span className="flex items-center gap-2 truncate">
            <Tag className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />
            <span className={cn('truncate', !selected && 'text-[#5C7599]')}>
              {selected ? selected.path : placeholder}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-[#041A3B] border-white/10 text-white"
        align="start"
      >
        <Command className="bg-[#041A3B] text-white">
          <CommandInput placeholder="Search categories…" className="text-white" data-testid={`${testId}-search`} />
          <CommandList>
            {loading ? (
              <div className="py-6 flex items-center justify-center text-[#A0B4CC] gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading categories…
              </div>
            ) : (
              <>
                <CommandEmpty className="text-[#A0B4CC] py-6 text-sm text-center">
                  No matches. Try a different search.
                </CommandEmpty>
                <CommandGroup>
                  {flat.map((c) => (
                    <CommandItem
                      key={c.id}
                      // The `value` is what cmdk fuzzy-matches against — include
                      // both the breadcrumb path AND any admin-tagged keywords.
                      value={`${c.path} ${c.keywords}`}
                      onSelect={() => {
                        onChange(c.id, c.name);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex items-center gap-2 cursor-pointer',
                        c.depth === 1 && 'pl-6 text-[#A0B4CC]'
                      )}
                      data-testid={`${testId}-option-${c.id}`}
                    >
                      <Check className={cn('w-4 h-4', value === c.id ? 'opacity-100 text-[#00E5FF]' : 'opacity-0')} />
                      <span className="truncate">{c.depth === 1 ? `↳ ${c.name}` : c.name}</span>
                      {c.depth === 0 && <span className="ml-auto text-[10px] text-[#5C7599] uppercase tracking-wide">top-level</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
